/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印实验远程流量配置解析
 */

export type BatchWatermarkExperimentEnvironment = 'test' | 'production'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * 解析批量编辑水印实验在当前环境的远程流量比例。
 *
 * KV 缺失、JSON 非法或比例类型错误时安全关闭实验，避免异常配置误放量。
 *
 * @param rawConfigValue - KV 返回的原始 JSON 字符串
 * @param environment - 当前运行环境
 * @returns 归一化后的 0～100 整数，非法配置返回 0
 *
 * @example
 * ```ts
 * parseBatchWatermarkExperimentRolloutPercentage(
 *   '{"test":{"rolloutPercentage":100},"production":{"rolloutPercentage":30}}',
 *   'production'
 * ) // 30
 * ```
 */
export const parseBatchWatermarkExperimentRolloutPercentage = (
  rawConfigValue: unknown,
  environment: BatchWatermarkExperimentEnvironment
): number => {
  if (typeof rawConfigValue !== 'string' || !rawConfigValue.trim()) {
    return 0
  }

  try {
    const parsedConfig: unknown = JSON.parse(rawConfigValue)
    if (!isRecord(parsedConfig)) {
      return 0
    }

    const environmentConfig = parsedConfig[environment]
    if (!isRecord(environmentConfig)) {
      return 0
    }

    const percentage = environmentConfig.rolloutPercentage
    if (typeof percentage !== 'number' || !Number.isFinite(percentage)) {
      return 0
    }

    return Math.min(100, Math.max(0, Math.floor(percentage)))
  } catch {
    return 0
  }
}
